// src/components/tenant/InviteAdminModal.tsx
// ✅ UPDATED: Minor tweaks for Cognito-native flow (removed sendEmail toggle)

import React, { useState, useEffect } from 'react';
import { 
  X, 
  Mail, 
  User, 
  Shield, 
  Send, 
  AlertCircle, 
  CheckCircle, 
  Info,
  UserPlus
} from 'lucide-react';
import { userProvisioningAPI, InviteAdminUserRequest } from '../../services/userProvisioningAPI';
import { TenantRecord } from '../../types/tenant';
import { useUserAttributes } from '../../hooks/useUserAttributes';

interface InviteAdminModalProps {
  isOpen: boolean;
  tenant: TenantRecord | { tenantId: string; organizationName: string };
  onClose: () => void;
  onInvitationSent: () => void;
}

// Role definitions with permissions
const AVAILABLE_ROLES = {
  'OrgAdmin': {
    label: 'Organization Administrator',
    description: 'Full access to manage department users, inventory, and settings',
    icon: Shield,
    permissions: ['Manage Users', 'Manage Inventory', 'Configure Settings', 'View Reports']
  },
  'InventoryManager': {
    label: 'Inventory Manager', 
    description: 'Manage equipment assignments, track inventory, and generate reports',
    icon: User,
    permissions: ['Manage Inventory', 'Issue Equipment', 'View Reports', 'Track Assignments']
  },
  'QuickAssign': {
    label: 'Quick Assign',
    description: 'Mobile-only access for quick equipment assignments at shift change',
    icon: User,
    permissions: ['Issue Equipment', 'View Available Items']
  }
};

const InviteAdminModal: React.FC<InviteAdminModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onInvitationSent
}) => {
  const { attributes: userAttributes } = useUserAttributes();
  const currentUserRole = userAttributes?.['custom:role'];

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'InventoryManager' // Default role
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Determine available roles based on current user
  const getAvailableRoles = () => {
    if (currentUserRole === 'SuperAdmin') {
      return ['OrgAdmin'];
    } else if (currentUserRole === 'OrgAdmin') {
      return ['OrgAdmin', 'InventoryManager', 'QuickAssign'];
    }
    return [];
  };

  const availableRoles = getAvailableRoles();

  useEffect(() => {
    if (currentUserRole === 'SuperAdmin') {
      setFormData(prev => ({ ...prev, role: 'OrgAdmin' }));
    } else if (currentUserRole === 'OrgAdmin') {
      setFormData(prev => ({ ...prev, role: 'InventoryManager' }));
    }
  }, [currentUserRole]);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;

    // Client-side validation
    const errors: string[] = [];
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.firstName.trim()) errors.push('First name is required');
    if (!formData.lastName.trim()) errors.push('Last name is required');
    if (!formData.role) errors.push('Role is required');
    
    if (!userProvisioningAPI.isValidEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!availableRoles.includes(formData.role)) {
      errors.push('Invalid role selected');
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const inviteData: InviteAdminUserRequest = {
        tenantId: tenant.tenantId,
        email: formData.email.toLowerCase().trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role as 'OrgAdmin' | 'InventoryManager' | 'QuickAssign',
        organizationName: tenant.organizationName
      };

      console.log('Sending user invitation:', inviteData);

      const response = await userProvisioningAPI.inviteAdminUser(inviteData);

      if (response.success && response.data) {
        const { message, isResend } = response.data;
        
        console.log('User invitation sent successfully');
        
        if (isResend) {
          setSuccess('Invitation email has been resent to ' + formData.email);
        } else {
          setSuccess(message);
        }
        
        onInvitationSent();
        
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        throw new Error(response.error?.message || 'Failed to send invitation');
      }
    } catch (err: any) {
      console.error('Error sending user invitation:', err);
      setError(err.message || 'Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: currentUserRole === 'SuperAdmin' ? 'OrgAdmin' : 'InventoryManager'
    });
    setError(null);
    setSuccess(null);
    onClose();
  };

  const getModalTitle = () => {
    if (currentUserRole === 'SuperAdmin') {
      return 'Invite Admin User';
    }
    return 'Invite User';
  };

  const getModalDescription = () => {
    if (currentUserRole === 'SuperAdmin') {
      return 'Create admin access for department management';
    }
    return 'Grant system access to department personnel';
  };

  const selectedRoleInfo = AVAILABLE_ROLES[formData.role as keyof typeof AVAILABLE_ROLES];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserPlus className="w-6 h-6 mr-3 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {getModalTitle()}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {tenant.organizationName} • {getModalDescription()}
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-blue-50 px-6 py-3 border-b border-blue-100">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">About User Invitations</p>
                <p className="mt-1">
                  The invited user will receive an email from <strong>noreply@qmleo.com</strong> with 
                  temporary credentials and setup instructions. They'll be required to change their 
                  password on first login and will have the selected role permissions.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Content */}
            <div className="bg-white px-6 py-4">
              {/* Error Message */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                    <div className="text-sm text-green-700">
                      <p className="font-medium">Invitation Sent Successfully!</p>
                      <p className="mt-1">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Fields */}
              {!success && (
                <div className="space-y-4">
                  {/* Email Address */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:opacity-50"
                      placeholder="user@department.gov"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This will be their login email address
                    </p>
                  </div>

                  {/* First Name */}
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:opacity-50"
                      placeholder="John"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:opacity-50"
                      placeholder="Smith"
                      required
                    />
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Role
                    </label>
                    {availableRoles.length > 1 ? (
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        disabled={loading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:opacity-50"
                        required
                      >
                        {availableRoles.map(roleKey => {
                          const role = AVAILABLE_ROLES[roleKey as keyof typeof AVAILABLE_ROLES];
                          return (
                            <option key={roleKey} value={roleKey}>
                              {role.label}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        <span className="text-sm text-gray-700">
                          {AVAILABLE_ROLES[availableRoles[0] as keyof typeof AVAILABLE_ROLES]?.label}
                        </span>
                      </div>
                    )}
                    
                    {/* Role Description */}
                    {selectedRoleInfo && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md">
                        <p className="text-xs text-gray-600 mb-2">{selectedRoleInfo.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedRoleInfo.permissions.map(permission => (
                            <span
                              key={permission}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {permission}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            {!success && (
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-3 sm:space-y-0">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Success Actions */}
            {success && (
              <div className="bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Done
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteAdminModal;
